import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableComponent } from '../../../../../shared/components/table/table.component';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { PageSizeComponent } from '../../../../../shared/components/page-size/page-size.component';
import { SearchInputComponent } from '../../../../../shared/components/search-input/search-input.component';
import { SearchableFilterComponent } from '../../../../../shared/components/searchable-filter/searchable-filter.component';
import { MenuComponentBase } from '../../../../../shared/base-classes/menu-component-base';
import { EmailLogEntry } from '../../models/email-log';
import { EmailLogService } from '../../services/email-log.service';
import { EmailDetailPopupComponent } from '../email-detail-popup/email-detail-popup.component';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-email-log',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    PaginationComponent,
    PageSizeComponent,
    SearchInputComponent,
    SearchableFilterComponent,
    EmailDetailPopupComponent,
  ],
  templateUrl: './email-log.component.html',
  styleUrl: './email-log.component.scss',
})
export class EmailLogComponent extends MenuComponentBase implements OnInit {
  private emailLogService = inject(EmailLogService);

  loading = false;
  totalEmails = 0;
  emailLogs: EmailLogEntry[] = [];
  searchQuery = '';
  contextFilter = '';
  selectedEmailId: number | null = null;
  showDetailPopup = false;

  currentPage = 1;
  pageSize = 50;

  tableColumns = [
    { header: 'Recipient', width: '18%' },
    { header: 'Subject', width: '28%' },
    { header: 'Context', width: '16%' },
    { header: 'Reference ID', width: '10%' },
    { header: 'Sent At', width: '18%' },
    { header: 'Actions', width: '10%' },
  ];

  contextFilterOptions: { option: string; value: string }[] = [
    { option: 'All', value: '' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadContexts();
    this.loadEmailLogs();
  }

  loadContexts(): void {
    this.emailLogService
      .getEmailLogContexts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contexts) => {
          this.contextFilterOptions = [
            { option: 'All', value: '' },
            ...contexts,
          ];
        },
      });
  }

  loadEmailLogs(): void {
    this.loading = true;
    const recipientEmail = this.searchQuery || undefined;
    const emailContext = this.contextFilter || undefined;

    this.emailLogService
      .getEmailLogs(
        this.currentPage,
        this.pageSize,
        emailContext,
        recipientEmail,
      )
      .subscribe({
        next: (res) => {
          this.emailLogs = res.results;
          this.totalEmails = res.count;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEmailLogs();
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadEmailLogs();
    }
  }

  onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.currentPage = 1;
    this.loadEmailLogs();
  }

  onContextFilterChange(value: string): void {
    this.contextFilter = value;
    this.currentPage = 1;
    this.loadEmailLogs();
  }

  openDetailPopup(emailId: number): void {
    this.selectedEmailId = emailId;
    this.showDetailPopup = true;
  }

  closeDetailPopup(): void {
    this.showDetailPopup = false;
    this.selectedEmailId = null;
  }

  formatContext(context: string): string {
    return context
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
